import { Tab } from '@mui/material'
import { Tabs } from '@workwave-tidal/core/components'
import { useSelector } from 'react-redux'

import { Tooltip } from '@/components/primitives/Tooltip'
import { selectUserConfiguration } from '@/features/domain/user'

import { useTexts } from './useTexts'

interface Props {
  tab: number
  setTab: (tab: number) => void
  disabled?: boolean
  testIdPrefix?: string
}

export function PreviewTabs(props: Props) {
  const { tab, setTab, disabled = false, testIdPrefix = 'order-form-tab__' } = props
  const texts = useTexts()

  const onTabChanged = (_event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue)
  }

  const userConfig = useSelector(selectUserConfiguration)
  const isSimulation = userConfig.planType === 'simulation'

  // notification and execution tabs should be disabled while in simulation
  // there are no order step notifications or execution events while in simulation
  // configuration and execution tabs should be disabled when in edit mode
  const configurationTabDisabled = disabled
  const notificationTabDisabled = isSimulation || disabled
  const executionTabDisabled = isSimulation || disabled

  // Tooltip logic:
  // - Configuration tab: no tooltip needed
  // - Notification/Execution tabs: show when disabled, with different messages for simulation vs edit mode
  const configurationTabTooltipDisabled = true
  const notificationTabTooltipDisabled = !notificationTabDisabled
  const executionTabTooltipDisabled = !executionTabDisabled

  // Select the appropriate tooltip text based on the mode
  const notificationTooltipText = isSimulation
    ? texts.notificationTabDisabledInSimulationTooltip
    : texts.notificationTabDisabledInEditModeTooltip
  const executionTooltipText = isSimulation
    ? texts.executionTabDisabledInSimulationTooltip
    : texts.executionTabDisabledInEditModeTooltip

  return (
    <Tabs
      value={tab}
      onChange={onTabChanged}
      variant="fullWidth"
      sx={{
        '& .Mui-selected': {
          color: 'primary.main',
        },
      }}
    >
      <Tab
        value={0}
        label={
          <Tooltip disabled={configurationTabTooltipDisabled} title="" placement="bottom">
            <>{texts.configuration}</>
          </Tooltip>
        }
        style={{ pointerEvents: 'auto' }}
        data-testid={`${testIdPrefix}configuration`}
        data-trackid={`${testIdPrefix}configuration`}
        disabled={configurationTabDisabled}
      />

      <Tab
        value={2}
        label={
          <Tooltip
            disabled={notificationTabTooltipDisabled}
            title={notificationTooltipText}
            placement="bottom"
          >
            <>{texts.communication}</>
          </Tooltip>
        }
        style={{ pointerEvents: 'auto' }} // Required to allow tooltip to work properly
        data-testid={`${testIdPrefix}notification`}
        data-trackid={`${testIdPrefix}notification`}
        disabled={notificationTabDisabled}
      />

      <Tab
        value={1}
        label={
          <Tooltip
            disabled={executionTabTooltipDisabled}
            title={executionTooltipText}
            placement="bottom"
          >
            <>{texts.execution}</>
          </Tooltip>
        }
        style={{ pointerEvents: 'auto' }} // Required to allow tooltip to work properly
        data-testid={`${testIdPrefix}execution`}
        data-trackid={`${testIdPrefix}execution`}
        disabled={executionTabDisabled}
      />
    </Tabs>
  )
}
